/**
 * Cliente del worker del decodificador (004 · US1 · T028).
 *
 * Una petición en vuelo como máximo: decodificar más rápido de lo que el worker responde solo
 * acumularía fotogramas viejos: si llega uno nuevo mientras hay otro en curso, **se descarta el
 * nuevo** (el siguiente fotograma de cámara llega en 33 ms de todos modos).
 *
 * El buffer RGBA se **transfiere**, no se copia (640 KB por fotograma con la reducción de `roi.ts`).
 * Tras transferirlo, el `ImageData` de origen queda inutilizable: por eso el bucle de captura crea
 * uno nuevo en cada vuelta.
 *
 * La fábrica del worker es inyectable para poder probar el bucle sin WebAssembly ni `Worker`.
 */
import type { DecodeRequest, DecoderMessage } from './decoder-protocol';

/** Lo mínimo de `Worker` que usa el cliente (un doble de pruebas lo implementa en 10 líneas). */
export interface DecoderWorkerLike {
  postMessage(message: DecodeRequest, transfer: Transferable[]): void;
  addEventListener(type: 'message', listener: (event: MessageEvent<DecoderMessage>) => void): void;
  terminate(): void;
}

export interface DecodeOutcome {
  /** Texto exacto del QR, o `null` si el fotograma no traía ninguno legible. */
  text: string | null;
  /** Lado del símbolo en la imagen reducida; `null` sin lectura. Telemetría (research R-4). */
  symbolSidePx: number | null;
  decodeMs: number;
}

export interface Decoder {
  /** `null` si el worker estaba ocupado (el fotograma se descarta) o si falló la lectura. */
  decode(image: ImageData): Promise<DecodeOutcome | null>;
  close(): void;
}

/** Worker real: módulo ES con la URL resuelta por Vite (`worker.format: 'es'` en la config). */
function defaultWorker(): DecoderWorkerLike {
  return new Worker(new URL('./decoder.worker.ts', import.meta.url), {
    type: 'module',
  }) as unknown as DecoderWorkerLike;
}

export interface DecoderOptions {
  createWorker?: () => DecoderWorkerLike;
}

export function createDecoder(options: DecoderOptions = {}): Decoder {
  const worker = (options.createWorker ?? defaultWorker)();
  const pending = new Map<number, (outcome: DecodeOutcome | null) => void>();
  let nextId = 1;
  let busy = false;
  let closed = false;

  worker.addEventListener('message', (event: MessageEvent<DecoderMessage>) => {
    const message = event.data;
    const resolve = pending.get(message.id);
    if (resolve === undefined) {
      return;
    }
    pending.delete(message.id);
    busy = false;
    if (message.type === 'decoded') {
      resolve({
        text: message.text,
        symbolSidePx: message.symbolSidePx,
        decodeMs: message.decodeMs,
      });
      return;
    }
    resolve(null);
  });

  return {
    decode(image) {
      if (closed || busy) {
        return Promise.resolve(null);
      }
      busy = true;
      const id = nextId;
      nextId += 1;
      return new Promise<DecodeOutcome | null>((resolve) => {
        pending.set(id, resolve);
        try {
          worker.postMessage(
            { type: 'decode', id, data: image.data, width: image.width, height: image.height },
            [image.data.buffer],
          );
        } catch {
          pending.delete(id);
          busy = false;
          resolve(null);
        }
      });
    },
    close() {
      if (closed) {
        return;
      }
      closed = true;
      for (const resolve of pending.values()) {
        resolve(null);
      }
      pending.clear();
      worker.terminate();
    },
  };
}
